import React, { useState, useEffect } from 'react';
import {
  CogIcon,
  CloudIcon,
  KeyIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { configAPI } from '../services/api';

const GlobalConfig = () => {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [testingAWS, setTestingAWS] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showingValues, setShowingValues] = useState({});
  const [newConfig, setNewConfig] = useState({
    key: '',
    value: '',
    description: '',
    type: 'string',
    encrypted: false,
    category: 'general'
  });
  const [showNewConfigForm, setShowNewConfigForm] = useState(false);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await configAPI.getAll();
      setConfigs(response.data);
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (category, key, newValue) => {
    try {
      const config = configs[category][key];
      await configAPI.update(key, {
        value: newValue,
        description: config.description,
        type: config.type,
        encrypted: config.encrypted,
        category: category
      });
      
      // Atualizar estado local
      setConfigs(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [key]: {
            ...prev[category][key],
            value: config.encrypted ? '********' : newValue
          }
        }
      }));
      
      setEditingConfig(null);
    } catch (error) {
      console.error('Erro ao atualizar configuração:', error);
      alert('Erro ao atualizar configuração: ' + error.response?.data?.error);
    }
  };

  const createConfig = async () => {
    try {
      await configAPI.create(newConfig);
      setNewConfig({
        key: '',
        value: '',
        description: '',
        type: 'string',
        encrypted: false,
        category: 'general'
      });
      setShowNewConfigForm(false);
      fetchConfigs();
    } catch (error) {
      console.error('Erro ao criar configuração:', error);
      alert('Erro ao criar configuração: ' + error.response?.data?.error);
    }
  };

  const testAWSConnection = async () => {
    setTestingAWS(true);
    try {
      const response = await configAPI.testAWS();
      alert(`✅ Conexão AWS funcionando!\nBucket: ${response.data.bucket}\nRegião: ${response.data.region}`);
    } catch (error) {
      alert('❌ Falha na conexão AWS:\n' + error.response?.data?.error);
    } finally {
      setTestingAWS(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'aws':
        return <CloudIcon className="h-5 w-5" />;
      case 'security':
        return <KeyIcon className="h-5 w-5" />;
      default:
        return <CogIcon className="h-5 w-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'aws':
        return 'bg-orange-100 text-orange-800';
      case 'security':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const toggleShowValue = (category, key) => {
    const configKey = `${category}.${key}`;
    setShowingValues(prev => ({
      ...prev,
      [configKey]: !prev[configKey]
    }));
  };

  const renderConfigValue = (category, key, config) => {
    const configKey = `${category}.${key}`;
    const isEditing = editingConfig === configKey;
    const isShowing = showingValues[configKey];
    
    if (isEditing) {
      return (
        <div className="flex items-center space-x-2">
          <input
            type={config.type === 'boolean' ? 'checkbox' : config.encrypted && !isShowing ? 'password' : 'text'}
            defaultValue={config.type === 'boolean' ? undefined : config.value === '********' ? '' : config.value}
            defaultChecked={config.type === 'boolean' ? config.value : undefined}
            className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const newValue = config.type === 'boolean' ? e.target.checked : e.target.value;
                updateConfig(category, key, newValue);
              } else if (e.key === 'Escape') {
                setEditingConfig(null);
              }
            }}
            onBlur={(e) => {
              const newValue = config.type === 'boolean' ? e.target.checked : e.target.value;
              updateConfig(category, key, newValue);
            }}
            autoFocus
          />
          <button
            onClick={() => setEditingConfig(null)}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2">
        <span
          className="flex-1 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
          onClick={() => setEditingConfig(configKey)}
        >
          {config.encrypted && !isShowing ? '********' : String(config.value)}
        </span>
        {config.encrypted && (
          <button
            onClick={() => toggleShowValue(category, key)}
            className="text-gray-400 hover:text-gray-600"
          >
            {isShowing ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <CogIcon className="h-8 w-8 mr-3 text-blue-600" />
          Configurações Globais
        </h1>
        <p className="mt-2 text-gray-600">
          Gerencie as configurações centralizadas do sistema
        </p>
      </div>

      {/* AWS Test Button */}
      {configs.aws && (
        <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-orange-900">Configurações AWS</h3>
              <p className="text-orange-700">Teste a conectividade com o S3</p>
            </div>
            <button
              onClick={testAWSConnection}
              disabled={testingAWS}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 flex items-center"
            >
              {testingAWS ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Testando...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Testar Conexão AWS
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* New Config Form */}
      {showNewConfigForm && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Nova Configuração</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chave</label>
              <input
                type="text"
                value={newConfig.key}
                onChange={(e) => setNewConfig({...newConfig, key: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="ex: nova_configuracao"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
              <input
                type="text"
                value={newConfig.value}
                onChange={(e) => setNewConfig({...newConfig, value: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <input
                type="text"
                value={newConfig.description}
                onChange={(e) => setNewConfig({...newConfig, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
              <select
                value={newConfig.category}
                onChange={(e) => setNewConfig({...newConfig, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="general">Geral</option>
                <option value="aws">AWS</option>
                <option value="security">Segurança</option>
                <option value="email">Email</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={newConfig.type}
                onChange={(e) => setNewConfig({...newConfig, type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="string">Texto</option>
                <option value="number">Número</option>
                <option value="boolean">Booleano</option>
                <option value="json">JSON</option>
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={newConfig.encrypted}
                onChange={(e) => setNewConfig({...newConfig, encrypted: e.target.checked})}
                className="mr-2"
              />
              <label className="text-sm font-medium text-gray-700">Criptografado</label>
            </div>
          </div>
          <div className="mt-4 flex space-x-3">
            <button
              onClick={createConfig}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Criar
            </button>
            <button
              onClick={() => setShowNewConfigForm(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Add New Config Button */}
      {!showNewConfigForm && (
        <div className="mb-6">
          <button
            onClick={() => setShowNewConfigForm(true)}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nova Configuração
          </button>
        </div>
      )}

      {/* Configurations by Category */}
      <div className="space-y-6">
        {Object.entries(configs).map(([category, categoryConfigs]) => (
          <div key={category} className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(category)} mr-3`}>
                  {getCategoryIcon(category)}
                  <span className="ml-1 capitalize">{category}</span>
                </span>
                <h2 className="text-lg font-medium text-gray-900 capitalize">
                  {category === 'aws' ? 'Amazon Web Services' : category}
                </h2>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {Object.entries(categoryConfigs).map(([key, config]) => (
                <div key={key} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-sm font-medium text-gray-900">{key}</h3>
                        {config.encrypted && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            <KeyIcon className="h-3 w-3 mr-1" />
                            Criptografado
                          </span>
                        )}
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          {config.type}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">{config.description}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="text-sm text-gray-900">
                      {renderConfigValue(category, key, config)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {Object.keys(configs).length === 0 && (
        <div className="text-center py-12">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma configuração encontrada</h3>
          <p className="mt-1 text-sm text-gray-500">Comece criando uma nova configuração.</p>
        </div>
      )}
    </div>
  );
};

export default GlobalConfig;